import type { ContentfulContentModel, ContentfulPageModel, ProductListViewModel } from 'autogen/swagger/all';
import { useIsPreview } from 'components/pages/Contentful/context/PreviewContext';
import { filterPublishedFields } from 'components/pages/Contentful/shared/ContentfulHelpers';
import ProductListPage from 'components/pages/PLP/ProductListPage';
import type { TypeProductListTemplateDetailed } from 'types/autogen/contentful';
import Preview from '../Preview';
import {
    useResolvedContentfulContentLiveUpdates,
    useResolvedContentfulPageLiveUpdates
} from '../shared/resolveContentfulResponse';
import Section from './Section';

function getContentfulContentFields(
    contentfulContent?: ContentfulPageModel
): TypeProductListTemplateDetailed['fields'] {
    const fallbackResult = { internalName: '', hideProductList: false };
    if (!contentfulContent) return fallbackResult;

    const { contentfulPage, customDataModel, isPreview } = contentfulContent;
    const contentfulResponse =
        contentfulPage && customDataModel ? useResolvedContentfulPageLiveUpdates(contentfulContent) : undefined;
    const contentfulFields = contentfulResponse?.resolvedData[0]?.fields?.template?.fields as
        | TypeProductListTemplateDetailed['fields']
        | undefined;

    return contentfulFields || fallbackResult;
}

function getContentfulSeoContentBlockFields(contentfulSeoContentBlock?: ContentfulContentModel) {
    if (!contentfulSeoContentBlock) return undefined;

    const { contentfulContent, customDataModel, isPreview } = contentfulSeoContentBlock;
    const contentfulResponse =
        contentfulContent && customDataModel
            ? useResolvedContentfulContentLiveUpdates(contentfulSeoContentBlock)[0]
            : undefined;

    if (!contentfulResponse?.fields?.content) return undefined;

    const contentfulFields = contentfulResponse?.fields?.content;

    return contentfulFields;
}

function PLPContent({ model }: { model: ProductListViewModel }) {
    const { contentfulContent, contentfulSeoContentBlock } = model;
    const { hideProductList, contentOverPLP, contentUnderPLP } = getContentfulContentFields(contentfulContent);
    const SEOContentBlockEntries = getContentfulSeoContentBlockFields(contentfulSeoContentBlock);

    const contentOverPLPRender = filterPublishedFields(contentOverPLP);
    const contentUnderPLPRender = filterPublishedFields(contentUnderPLP);

    return (
        <ProductListPage
            model={model}
            hideProductList={hideProductList}
            contentOverPLP={contentOverPLP && <Section entries={contentOverPLPRender} />}
            contentUnderPLP={contentUnderPLP && <Section entries={contentUnderPLPRender} />}
            SEOContentBlock={SEOContentBlockEntries && <Section entries={SEOContentBlockEntries} />}
        />
    );
}

export default function ContentfulPLP({ model }: { model: ProductListViewModel }) {
    const { contentfulContent } = model;

    if (contentfulContent?.isPreview) {
        return (
            <Preview model={contentfulContent}>
                <PLPContent model={model} />
            </Preview>
        );
    }

    return <PLPContent model={model} />;
}
